import { useEffect, useRef, useState } from "react";
import { Check, Edit2, RotateCcw, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export const MAX_LOW_STOCK_THRESHOLD = 100000;

interface LowStockAlertEditorProps {
  /** The alert set on this item; null means it uses `inheritedValue` */
  value: number | null;
  /** Alert used when `value` is null (e.g. the product's alert for a variation) */
  inheritedValue?: number;
  /** Show a "use default" action that saves null (variations only) */
  allowReset?: boolean;
  onSave: (value: number | null) => Promise<void>;
}

/**
 * Inline editor for a low stock alert threshold.
 */
const LowStockAlertEditor: React.FC<LowStockAlertEditorProps> = ({
  value,
  inheritedValue,
  allowReset = false,
  onSave,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isInherited = value === null || value === undefined;
  const displayValue = isInherited ? inheritedValue ?? 0 : value;

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const startEdit = () => {
    setDraft(String(displayValue));
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async (next: number | null) => {
    if (next !== null) {
      if (!/^\d+$/.test(draft.trim())) {
        setError(t("Enter a whole number"));
        return;
      }
      if (next > MAX_LOW_STOCK_THRESHOLD) {
        setError(t("Too large"));
        return;
      }
      if (next === value) {
        setEditing(false);
        return;
      }
    }

    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
      setError(null);
    } catch {
      // The caller shows the error toast; keep the editor open so the user can retry
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="font-semibold">{displayValue}</span>
        {allowReset && isInherited && (
          <span className="text-xs text-gray-400">({t("default")})</span>
        )}
        <button
          type="button"
          onClick={startEdit}
          className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900"
          title={t("Edit alert")}
          aria-label={t("Edit alert")}
        >
          <Edit2 size={13} />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col">
      <span className="inline-flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          min={0}
          max={MAX_LOW_STOCK_THRESHOLD}
          step={1}
          inputMode="numeric"
          value={draft}
          disabled={saving}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save(Number(draft));
            if (e.key === "Escape") cancel();
          }}
          className={`h-8 w-20 rounded border-2 px-1 text-center text-sm font-semibold focus:outline-none disabled:bg-gray-100 ${
            error ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-black"
          }`}
          aria-label={t("Low stock alert")}
        />
        <button
          type="button"
          onClick={() => save(Number(draft))}
          disabled={saving}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-black text-white transition-colors hover:bg-gray-700 disabled:opacity-60"
          aria-label={t("Save")}
        >
          {saving ? (
            <span
              className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent"
              style={{ animation: "spin 1s linear infinite" }}
            />
          ) : (
            <Check size={14} />
          )}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-60"
          aria-label={t("Cancel")}
        >
          <X size={14} />
        </button>
        {allowReset && !isInherited && (
          <button
            type="button"
            onClick={() => save(null)}
            disabled={saving}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-60"
            title={t("Use product default")}
            aria-label={t("Use product default")}
          >
            <RotateCcw size={14} />
          </button>
        )}
      </span>
      {error && <span className="mt-1 text-xs text-red-600">{error}</span>}
    </span>
  );
};

export default LowStockAlertEditor;
